import { Injectable, EventEmitter, Renderer2, RendererFactory2  } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Group } from '../models/endpoint-group.model';
import { Router, NavigationStart } from '@angular/router';
import { AppSettings } from '../constants/appSettings.constants';
import { SidebarAction, ContentType, APIDomainVersion, APIDomain } from '../models/site.model';
import { StrapiService } from './strapi.service';

@Injectable({
  providedIn: 'root',
})
export class SiteService  {

  private settings = AppSettings;
  private renderer: Renderer2;
  private docsNavLoaded = false;

  // Domain version tracking per domain
  private lastCoreVersion = '';
  private lastAllyVersion = '';

  // Cached domain versions observable (single HTTP call)
  private domainVersionsCache$: Observable<any> | null = null;

  // Define observables.
  private navSourceSubject$ = new BehaviorSubject<Group[]>([]);
  currentNavEndpoints$ = this.navSourceSubject$.asObservable();

  private docsNavSourceSubject$ = new BehaviorSubject<Group[]>([]);
  currentNavDocs$ = this.docsNavSourceSubject$.asObservable();

  private activeItemSourceSubject$ = new BehaviorSubject<string>('');
  activeItem$ = this.activeItemSourceSubject$.asObservable();

  private activeGroupSourceSubject$ = new BehaviorSubject<string>('');
  activeGroup$ = this.activeGroupSourceSubject$.asObservable();

  private domainVersionSourceSubject$ = new BehaviorSubject<APIDomainVersion>(this.settings.general.defaultDomainVersion);
  currentDomainVersion$ = this.domainVersionSourceSubject$.asObservable();

  private coreVersionsSubject$ = new BehaviorSubject<string[]>([]);
  coreVersions$ = this.coreVersionsSubject$.asObservable();

  private allyVersionsSubject$ = new BehaviorSubject<string[]>([]);
  allyVersions$ = this.allyVersionsSubject$.asObservable();

  private navOpenSourceSubject$ = new BehaviorSubject<boolean>(window.innerWidth > 768);
  navOpen$ = this.navOpenSourceSubject$.asObservable();

  private themeSourceSubject$ = new BehaviorSubject<string>(this.loadTheme());
  theme$ = this.themeSourceSubject$.asObservable();

  private sidebarActionSubject$ = new Subject<SidebarAction>();
  sidebarActions$ = this.sidebarActionSubject$.asObservable();

  private contentTypeSubject$ = new BehaviorSubject<ContentType>('api');
  contentType$ = this.contentTypeSubject$.asObservable();

  public closeNavGroupsEvent: EventEmitter<void> = new EventEmitter();

  public domainVersionChangedEvent: EventEmitter<void> = new EventEmitter();


  constructor(
    private router: Router,
    private rendererFactory: RendererFactory2,
    private strapiService: StrapiService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.updateContentType(this.router.url);
    this.listenToRouting();
    this.loadDomainVersions();
  }
 
  toggleTheme() {
    const theme = (this.themeSourceSubject$.value === 'dark-mode') ? 'light-mode' : 'dark-mode';
    
    localStorage.setItem('theme', theme);
    this.themeSourceSubject$.next(theme);
  }

  loadTheme() {
    const storedTheme = localStorage.getItem('theme') || 'dark-mode';
    return storedTheme;
  }

  toggleSidebar() {
    let navOpenState = !this.navOpenSourceSubject$.value;
    this.navOpenSourceSubject$.next(navOpenState);
    this.sidebarActionSubject$.next(SidebarAction.Toggle);
  }

  closeSidebar() {
    this.navOpenSourceSubject$.next(false);
    this.sidebarActionSubject$.next(SidebarAction.Close);
  }

  openSidebar() {
    this.navOpenSourceSubject$.next(true);
    this.sidebarActionSubject$.next(SidebarAction.Open);
  }

  /**
   * Switch between core and ally domains.
   * Restores the last version that was active for the requested domain.
   */
  changeDomain(domain: APIDomain): void {
    const version = domain === 'core' ? this.lastCoreVersion : this.lastAllyVersion;
    this.applyDomainVersion(domain, version);
  }

  /**
   * Change the API version within the currently active domain.
   * Tracks the selection per-domain so it can be restored on domain switch.
   */
  changeVersion(version: string): void {
    const currentDomainVersion = this.domainVersionSourceSubject$.value;
    const domain: APIDomain = currentDomainVersion.startsWith('ally_') ? 'ally' : 'core';
    this.applyDomainVersion(domain, version);
  }

  private applyDomainVersion(domain: APIDomain, version: string): void {
    if (!version) return;
    if (domain === 'core') {
      this.lastCoreVersion = version;
    } else {
      this.lastAllyVersion = version;
    }
    const domainVersion = `${domain}_${version}` as APIDomainVersion;
    this.domainVersionSourceSubject$.next(domainVersion);
    this.domainVersionChangedEvent.emit();
  }

  private loadDomainVersions(): void {
    if (this.domainVersionsCache$) return;

    this.domainVersionsCache$ = this.strapiService.getAllDomainVersions().pipe(
      shareReplay(1)
    );

    this.domainVersionsCache$.subscribe((response: any) => {
      const items: any[] = response?.data ?? [];
      const active = items.filter((i: any) => i.Active);
      const coreVersions = active.filter((i: any) => i.Domain === 'core').map((i: any) => i.Version);
      const allyVersions = active.filter((i: any) => i.Domain === 'ally').map((i: any) => i.Version);

      this.coreVersionsSubject$.next(coreVersions);
      this.allyVersionsSubject$.next(allyVersions);

      // Initialise per-domain last-used versions to the highest available
      this.lastCoreVersion = this.getHighestVersion(coreVersions);
      this.lastAllyVersion = this.getHighestVersion(allyVersions);

      // Apply the highest version for the current domain
      const currentDomainVersion = this.domainVersionSourceSubject$.value;
      const currentDomain: APIDomain = currentDomainVersion.startsWith('ally_') ? 'ally' : 'core';
      const version = currentDomain === 'core' ? this.lastCoreVersion : this.lastAllyVersion;
      if (version) {
        this.applyDomainVersion(currentDomain, version);
      }
    });
  }

  private getHighestVersion(versions: string[]): string {
    if (!versions.length) return '';
    return [...versions].sort((a, b) => {
      const partsA = a.split('.').map(Number);
      const partsB = b.split('.').map(Number);
      for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
        const diff = (partsB[i] || 0) - (partsA[i] || 0);
        if (diff !== 0) return diff;
      }
      return 0;
    })[0];
  }
  
  changeNav(nav: Group[]) {
    this.navSourceSubject$.next(nav);
  }
  
  changeActiveNavItem(item: string, groupCode: string, contentType: ContentType) {
    this.activeItemSourceSubject$.next(item);
    this.activeGroupSourceSubject$.next(groupCode);
    // console.log('Active nav changed:', { item, groupCode, contentType });
  }
  
  refreshDocsNav() {
    this.loadDocsNav();
  }

  private listenToRouting() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.updateContentType(event.url);
        
        if (!event.url.startsWith('/e/') && 
            !event.url.startsWith('/docs/')) 
        {
          this.resetActiveNav();
        }
      }
    });
  }

  private updateContentType(url: string) {
    const contentType: ContentType = ( url.startsWith('/docs') || url.startsWith('/preview') ) ? 'docs' : 'api';
    this.contentTypeSubject$.next(contentType);

    if (contentType === 'docs' && !this.docsNavLoaded) {
      this.loadDocsNav();
    }
  }

  private loadDocsNav() {
    this.strapiService.getSideNavDocs().subscribe({
      next: (docs) => {
        this.docsNavSourceSubject$.next(docs);
        this.docsNavLoaded = true;
      },
      error: (err) => {
        console.error('Failed to load docs navigation:', err);
      }
    });
  }

  private resetActiveNav() {
    this.activeItemSourceSubject$.next('none');
    this.activeGroupSourceSubject$.next('none');
    this.closeNavGroupsEvent.emit();
  }

  updateTheme( theme:string ) {
    const classToRemove = (theme === 'dark-mode') ? 'light-mode' : 'dark-mode';
    const aplClassToRemove = (theme === 'dark-mode') ? 'light' : 'dark';
    const aplClassToAdd = (theme === 'dark-mode') ? 'dark' : 'light';
    const htmlEl = document.documentElement;

    if (document.body.classList.contains(classToRemove)) {
      this.renderer.removeClass(document.body, classToRemove);
    }

    if (!document.body.classList.contains(theme)) {
      this.renderer.addClass(document.body, theme);
    }

    if (htmlEl.classList.contains(aplClassToRemove)) {
      this.renderer.removeClass(htmlEl, aplClassToRemove);
    }

    if (!htmlEl.classList.contains(aplClassToAdd)) {
      this.renderer.addClass(htmlEl, aplClassToAdd);
    }
  }

  

}